<template>
  <div
    class="free-textarea"
    :class="{
      'cursor-not-allowed opacity-50': $attrs.disabled,
    }"
  >
    <label
      v-if="label || Boolean($slots.label)"
      :for="id"
      class="text-text-neutral-tertiary mb-3 block text-xs font-medium"
    >
      <slot name="label"> {{ label }} </slot>
    </label>
    <div
      class="free-textarea__wrapper flex w-full items-start border border-solid px-4 py-3 duration-150"
      :class="{
        'free-textarea__wrapper_show-counter': showCounter,
        'border-transparent': !hasError && !isFocused,
        'border-background-negative-accent': hasError,
        'border-background-positive-primary': isFocused && !hasError,
        'min-h-[80px] rounded-xl': size === 'md',
        'min-h-[100px] rounded-xl': size === 'lg',
        'min-h-[120px] rounded-2xl': size === '2xl',
      }"
    >
      <div class="block grow">
        <div
          :class="[
            'free-textarea__textarea-container',
            {
              'free-textarea__textarea-container_has-prepend-slot': Boolean(
                $slots.prepend,
              ),
            },
          ]"
        >
          <div v-if="Boolean($slots.prepend)" class="mr-3 pt-2">
            <slot name="prepend" />
          </div>
          <textarea
            v-bind="$attrs"
            :id="id"
            ref="textareaRef"
            :value="modelValue"
            :placeholder="effectivePlaceholder"
            :rows="rows"
            :maxlength="maxlength"
            :class="[
              'free-textarea__textarea',
              { 'w-[98%]': isClearable || Boolean($slots.append) },
            ]"
            @input="handleInput"
            @focus="isFocused = true"
            @blur="isFocused = false"
          />
        </div>
      </div>
      <Transition name="textarea-fade">
        <div
          v-if="isClearable && Boolean($slots.clearButton) && isFocused"
          class="text-text-neutral-quaternary mx-1 mt-3 cursor-pointer"
          @click="handleClearButtonClick"
        >
          <slot name="clearButton" />
        </div>
      </Transition>
      <div v-if="Boolean($slots.append)" class="mx-2 mt-3">
        <slot name="append" v-bind="{ isClearable, handleClearButtonClick }" />
      </div>
      <div
        v-if="showCounter"
        class="free-textarea__counter text-text-neutral-quaternary absolute bottom-2 right-4 text-xs"
        aria-hidden="true"
      >
        {{ currentLength }} / {{ maxlength }}
      </div>
    </div>
    <Transition name="textarea-fade">
      <div v-if="hasError" class="text-text-negative-primary mt-2 text-xs">
        <slot name="errorMessage" />
      </div>
    </Transition>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, useAttrs } from 'vue';
  import { uuid4 as uuid } from '@sentry/utils';

  type TextareaSize = 'md' | 'lg' | '2xl';

  const props = withDefaults(
    defineProps<{
      modelValue?: string;
      clearable?: boolean;
      label?: string;
      size?: TextareaSize;
      hasError?: boolean;
      rows?: number;
      placeholder?: string;
      maxlength?: number;
    }>(),
    {
      clearable: true,
      modelValue: '',
      label: '',
      hasError: false,
      size: 'md',
      rows: 3,
      placeholder: 'Enter text...',
      maxlength: undefined,
    },
  );
  const emit = defineEmits<{
    'update:modelValue': [string];
  }>();

  const attrs = useAttrs();
  const effectivePlaceholder = computed(
    () => (attrs.placeholder as string | undefined) ?? props.placeholder,
  );

  const id = uuid();
  const textareaRef = ref<HTMLTextAreaElement>();
  const isFocused = ref(false);
  const isClearable = computed(
    () => props.clearable && Boolean(props.modelValue),
  );

  const showCounter = computed(() => props.maxlength != null);
  const currentLength = computed(() => (props.modelValue ?? '').length);

  const handleInput = (event: Event) => {
    let { value } = event.target as HTMLTextAreaElement;
    if (props.maxlength != null && value.length > props.maxlength) {
      value = value.slice(0, props.maxlength);
    }
    emit('update:modelValue', value);
  };

  const handleClearButtonClick = () => {
    emit('update:modelValue', '');
    textareaRef.value?.focus();
  };

  defineExpose({
    textareaRef,
  });
</script>

<style scoped>
  .free-textarea ::placeholder {
    @apply text-text-neutral-quaternary;
  }

  .free-textarea ::placeholder,
  .free-textarea__textarea {
    @apply text-base not-italic;
  }

  .free-textarea__wrapper {
    position: relative;
    background-color: var(
      --input-background,
      rgb(var(--situational-lightgrey))
    );
    border-radius: var(--input-border-radius, 0.75rem);
  }

  .free-textarea__wrapper_show-counter {
    padding-bottom: 1.5rem;
  }

  .free-textarea__textarea-container {
    @apply w-full;
  }

  .free-textarea__textarea-container_has-prepend-slot {
    @apply flex items-start;
  }

  .free-textarea__textarea-container_has-prepend-slot .free-textarea__textarea {
    @apply p-0;
  }

  .free-textarea__textarea {
    @apply text-text-neutral-primary w-full resize-y border-none px-0 outline-none;
    background-color: var(
      --input-background,
      rgb(var(--situational-lightgrey))
    );
  }

  .free-textarea__textarea:disabled {
    @apply text-text-neutral-quaternary cursor-not-allowed;
    -webkit-text-fill-color: theme('colors.text.neutral.quaternary');
    opacity: 1;
  }

  .textarea-fade-enter-active,
  .textarea-fade-leave-active {
    transition: opacity 0.15s;
  }

  .textarea-fade-enter-from,
  .textarea-fade-leave-to {
    opacity: 0;
  }
</style>
